import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectorClientes from "../../ComponentesGlobales/SelectorClientes";
import BuscadorMercaderias from "../../ComponentesGlobales/BuscadorMercaderias";
import { useStorePos } from "./useStorePos";
import { formatearMoneda } from "../../config/moneda";
import { calcularVentaLinea, calcularVentaResumen } from "../../servicios/ventas";
import { calcularNetoDesdePrecioFinal, formatearCategoriaFiscal } from "../../servicios/categoriasFiscales";
import { requiereCuit, validarClienteNuevo } from "../../servicios/facturacionFiscal";
import { puedeCrearClientes } from "../../servicios/sesionSeguridad";

const CLIENTE_NUEVO_VACIO = {
  Id: 0,
  sCodigo: "",
  sDescripcion: "",
  CondicionIVAId: "",
  Documento: "",
  TipoDocumentoId: "",
};

export const LIMITE_DOCUMENTO_CLIENTE_NUEVO = 10_000_000;

const obtenerDatoCliente = (cliente, campos, valorAlternativo) => {
  for (const campo of campos) {
    const valor = cliente?.[campo];
    if (valor !== undefined && valor !== null && String(valor).trim() !== "") {
      return valor;
    }
  }
  return valorAlternativo;
};

const PosVenta = ({ modoRapido = false }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "es-AR";
  const puedeCrearCliente = puedeCrearClientes();
  const [editorProductoVisible, setEditorProductoVisible] = useState(modoRapido);
  const [datosExpandidos, setDatosExpandidos] = useState(false);
  const [clienteNuevo, setClienteNuevo] = useState(null);
  const [erroresClienteNuevo, setErroresClienteNuevo] = useState({});
  const Cliente = useStorePos((state) => state.Cliente);
  const ModoCliente = useStorePos((state) => state.ModoCliente);
  const ClienteConsumidorFinalId = useStorePos((state) => state.ClienteConsumidorFinalId);
  const ConsumidorFinalCodigo = useStorePos((state) => state.ConsumidorFinalCodigo);
  const ConsumidorFinalDescripcion = useStorePos((state) => state.ConsumidorFinalDescripcion);
  const SolicitaIdentificacionFiscal = useStorePos((state) => state.SolicitaIdentificacionFiscal);
  const MontoConsumidorFinalSinIdentificar = useStorePos((state) => state.MontoConsumidorFinalSinIdentificar);
  const RequiereIdentificacionFiscal = useStorePos((state) => state.RequiereIdentificacionFiscal);
  const ClienteSeleccionRequerida = useStorePos((state) => state.ClienteSeleccionRequerida);
  const ListaClientes = useStorePos((state) => state.ListaClientes);
  const BuscandoClientes = useStorePos((state) => state.BuscandoClientes);
  const ErrorClientes = useStorePos((state) => state.ErrorClientes);
  const TiposDocumento = useStorePos((state) => state.TiposDocumento);
  const CondicionesIVA = useStorePos((state) => state.CondicionesIVA);
  const CargandoTiposDocumento = useStorePos((state) => state.CargandoTiposDocumento);
  const CargandoCondicionesIVA = useStorePos((state) => state.CargandoCondicionesIVA);
  const GuardandoCliente = useStorePos((state) => state.GuardandoCliente);
  const TicketLineas = useStorePos((state) => state.TicketLineas);
  const IvaMercaderia = useStorePos((state) => state.IvaMercaderia);

  const Mercaderia = useStorePos((state) => state.Mercaderia);
  const ListaMercaderias = useStorePos((state) => state.ListaMercaderias);
  const Cantidad = useStorePos((state) => state.Cantidad);
  const PrecioUnitario = useStorePos((state) => state.PrecioUnitario);
  const BuscandoPrecio = useStorePos((state) => state.BuscandoPrecio);
  const ErrorPrecio = useStorePos((state) => state.ErrorPrecio);
  const DescuentoLinea = useStorePos((state) => state.DescuentoLinea);
  const ArticuloLibreUnidad = useStorePos((state) => state.ArticuloLibreUnidad);
  const ListaUnidades = useStorePos((state) => state.ListaUnidades);
  const BuscandoUnidades = useStorePos((state) => state.BuscandoUnidades);
  const ListaCategoriasFiscales = useStorePos((state) => state.ListaCategoriasFiscales);
  const CategoriaFiscalProductoNuevo = useStorePos((state) => state.CategoriaFiscalProductoNuevo);
  const CargandoCategoriasFiscales = useStorePos((state) => state.CargandoCategoriasFiscales);
  const ErrorCategoriasFiscales = useStorePos((state) => state.ErrorCategoriasFiscales);
  const OpcionesFacturacion = useStorePos((state) => state.OpcionesFacturacion);
  const NumeradorId = useStorePos((state) => state.NumeradorId);
  const CargandoOpcionesFacturacion = useStorePos((state) => state.CargandoOpcionesFacturacion);
  const ErrorOpcionesFacturacion = useStorePos((state) => state.ErrorOpcionesFacturacion);
  const CondicionIVASeleccionada = useStorePos((state) => state.CondicionIVASeleccionada);
  const clienteSelectorRef = useRef(null);
  const productoSelectorRef = useRef(null);

  useEffect(() => {
    // La seleccion del producto inicia su consulta. Los cambios de cliente se
    // resuelven en setCliente para limpiar el importe de forma inmediata.
    if (Mercaderia && typeof Mercaderia === "object" && Mercaderia.Id) {
      useStorePos.getState().getPrecio();
      return;
    }
    useStorePos.setState({ BuscandoPrecio: false, ErrorPrecio: "", IvaMercaderia: null });
  }, [Mercaderia]);

  useEffect(() => {
    if (ClienteSeleccionRequerida) {
      window.setTimeout(() => clienteSelectorRef.current?.querySelector("input")?.focus(), 80);
    }
  }, [ClienteSeleccionRequerida]);

  const mercaderiaSel = useMemo(
    () => ListaMercaderias?.find((x) => x.Id === Mercaderia?.Id),
    [ListaMercaderias, Mercaderia]
  );
  // Mostramos el stock como aviso; el POS permite vender aunque no alcance.
  const esProductoExistente = Boolean(Mercaderia && typeof Mercaderia === "object" && Number(Mercaderia.Id) > 0);
  const descripcionArticuloNuevo = typeof Mercaderia === "string" ? Mercaderia.trim() : "";
  const esArticuloNuevo = !esProductoExistente && descripcionArticuloNuevo.length > 0;
  const stockDisponible = Number(Mercaderia?.sExtra ?? mercaderiaSel?.sExtra ?? 0);
  const stockTexto = esArticuloNuevo
    ? t("pos.newItem")
    : !esProductoExistente
      ? t("pos.selectValue")
      : stockDisponible > 0 ? stockDisponible : t("pos.outOfStock");
  const stockEstado = esArticuloNuevo
    ? "neutral"
    : !esProductoExistente
      ? "neutral"
      : stockDisponible > 0 ? "disponible" : "sin-stock";
  const cantidadNumero = Number(Cantidad) || 0;
  const precioNumero = Number(PrecioUnitario) || 0;
  const descuentoNumero = Number(DescuentoLinea) || 0;
  const precioCalculo = esArticuloNuevo
    ? calcularNetoDesdePrecioFinal(precioNumero, CategoriaFiscalProductoNuevo)
    : precioNumero;
  const descuentoCalculo = esArticuloNuevo
    ? calcularNetoDesdePrecioFinal(descuentoNumero, CategoriaFiscalProductoNuevo)
    : descuentoNumero;
  const calculoLinea = calcularVentaLinea({
    Cantidad: cantidadNumero,
    PrecioUnitario: precioCalculo,
    DescuentoLinea: descuentoCalculo,
    ConSinIVA: esArticuloNuevo
      ? Boolean(CategoriaFiscalProductoNuevo?.ConSinIVA)
      : esProductoExistente && IvaMercaderia?.valido && IvaMercaderia.ConSinIVA,
    PorcentajeIVA: esArticuloNuevo
      ? CategoriaFiscalProductoNuevo?.PorcentajeIVA
      : esProductoExistente ? IvaMercaderia?.PorcentajeIVA : 0,
  });
  const totalLinea = calculoLinea.subtotal + calculoLinea.ivaLinea;
  const totalLineaTexto = formatearMoneda(totalLinea);
  const totalVenta = useMemo(() => calcularVentaResumen(TicketLineas).TotalConIva, [TicketLineas]);
  const clienteSeleccionado = Number(Cliente?.Id) > 0;
  const clienteNuevoActivo = Boolean(clienteNuevo?.sDescripcion);
  const listaClientesSeleccionables = useMemo(
    () => ListaClientes.filter((item) => Number(item.Id) !== Number(ClienteConsumidorFinalId)),
    [ClienteConsumidorFinalId, ListaClientes]
  );
  const detalleCliente = useMemo(() => {
    const vendedorLocal =
      localStorage.getItem("OperadorNombre") ||
      localStorage.getItem("NombreOperador") ||
      localStorage.getItem("Nombre") ||
      t("pos.mainCashRegister");
    const razonSocial = obtenerDatoCliente(
      Cliente,
      ["RazonSocial", "sRazonSocial", "sDescripcion", "Descripcion", "Nombre"],
      clienteSeleccionado
        ? t("pos.selectedCustomer")
        : ConsumidorFinalDescripcion || t("pos.finalConsumer")
    );

    return {
      codigo: obtenerDatoCliente(
        Cliente,
        ["Codigo", "sCodigo", "ClienteCodigo"],
        clienteSeleccionado
          ? `CLI-${String(Cliente.Id).padStart(4, "0")}`
          : ConsumidorFinalCodigo || "CF"
      ),
      razonSocial,
      tipoDocumento: obtenerDatoCliente(
        Cliente,
        ["sTipoDocumento", "TipoDocumento", "DocumentoTipo"],
        clienteSeleccionado ? t("pos.document") : t("pos.genericCustomer")
      ),
      documento: obtenerDatoCliente(
        Cliente,
        ["sDocumento", "Documento", "NroDocumento", "NumeroDocumento", "CUIT", "Cuit", "sCUIT"],
        "—"
      ),
      condicionIva: obtenerDatoCliente(
        Cliente,
        ["sCondicionIVA", "CondicionIva", "CondicionIVA", "sCondicionIva", "IVA"],
        clienteSeleccionado ? t("pos.accordingToTaxSettings") : t("pos.finalConsumer")
      ),
      vendedor: obtenerDatoCliente(
        Cliente,
        ["Vendedor", "sVendedor", "VendedorNombre"],
        vendedorLocal
      ),
      usaDatosTemporales: clienteSeleccionado &&
        !Cliente?.sDocumento && !Cliente?.Documento && !Cliente?.NroDocumento && !Cliente?.CUIT && !Cliente?.Cuit,
    };
  }, [Cliente, ConsumidorFinalCodigo, ConsumidorFinalDescripcion, clienteSeleccionado, t]);
  const opcionFacturacion = useMemo(
    () => OpcionesFacturacion.find((opcion) => Number(opcion.NumeradorId) === Number(NumeradorId)),
    [NumeradorId, OpcionesFacturacion]
  );
  const fechaComprobante = new Intl.DateTimeFormat(locale).format(new Date());
  const numeroComprobante = opcionFacturacion?.ProximoNumero
    ? String(opcionFacturacion.ProximoNumero).padStart(8, "0")
    : t("pos.pendingValue");

  const condicionClienteNuevo = useMemo(
    () => CondicionesIVA.find((item) => Number(item.Id) === Number(clienteNuevo?.CondicionIVAId)),
    [CondicionesIVA, clienteNuevo?.CondicionIVAId]
  );
  const clienteNuevoRequiereDocumento = totalVenta > LIMITE_DOCUMENTO_CLIENTE_NUEVO;
  const clienteNuevoRequiereCuit = Boolean(
    clienteNuevoRequiereDocumento &&
    (SolicitaIdentificacionFiscal || (condicionClienteNuevo && requiereCuit(condicionClienteNuevo)))
  );

  useEffect(() => {
    if (clienteNuevoActivo && clienteNuevoRequiereDocumento) {
      useStorePos.getState().cargarTiposDocumento();
    }
  }, [clienteNuevoActivo, clienteNuevoRequiereDocumento]);

  const alternarEditorProducto = () => setEditorProductoVisible((visible) => !visible);

  const iniciarClienteNuevo = (nombre) => {
    if (!puedeCrearCliente) return false;
    const descripcion = String(nombre ?? "").trim();
    if (!descripcion) return false;
    setClienteNuevo({ ...CLIENTE_NUEVO_VACIO, sDescripcion: descripcion });
    setErroresClienteNuevo({});
    if (clienteNuevoRequiereDocumento) useStorePos.getState().cargarTiposDocumento();
    useStorePos.getState().cargarCondicionesIVA();
    useStorePos.getState().setCliente(null);
    return { ...CLIENTE_NUEVO_VACIO, sDescripcion: descripcion };
  };

  const seleccionarCliente = (cliente) => {
    if (!cliente) {
      if (!clienteNuevoActivo) useStorePos.getState().setCliente(null);
      return;
    }
    setClienteNuevo(null);
    setErroresClienteNuevo({});
    useStorePos.getState().setCliente(cliente);
  };

  const cambiarClienteNuevo = (campo, valor) => {
    setClienteNuevo((actual) => ({ ...actual, [campo]: valor }));
    setErroresClienteNuevo((actual) => ({ ...actual, [campo]: "" }));
  };

  const cancelarClienteNuevo = () => {
    setClienteNuevo(null);
    setErroresClienteNuevo({});
    useStorePos.getState().setConsumidorFinal();
  };

  const guardarClienteNuevo = async () => {
    if (!puedeCrearCliente) return;
    const validacion = validarClienteNuevo(clienteNuevo, TiposDocumento, CondicionesIVA, {
      exigirCuit: clienteNuevoRequiereDocumento && SolicitaIdentificacionFiscal,
      exigirDocumento: clienteNuevoRequiereDocumento,
    });
    setErroresClienteNuevo(validacion.errores);
    if (!validacion.valido) return;
    const creado = await useStorePos.getState().crearCliente(clienteNuevo, {
      exigirCuit: clienteNuevoRequiereDocumento && SolicitaIdentificacionFiscal,
      exigirDocumento: clienteNuevoRequiereDocumento,
    });
    if (creado) {
      setClienteNuevo(null);
      setErroresClienteNuevo({});
    }
  };

  const cambiarProductoEditor = (valor) => {
    const store = useStorePos.getState();
    const productoAnteriorEraCatalogado = Boolean(
      store.Mercaderia && typeof store.Mercaderia === "object" && Number(store.Mercaderia.Id) > 0
    );
    const esTextoLibre = typeof valor === "string";
    useStorePos.setState({
      Mercaderia: valor || null,
      ...(esTextoLibre && productoAnteriorEraCatalogado
        ? {
            PrecioUnitario: 0,
            DescuentoLinea: 0,
            ErrorPrecio: "",
            IvaMercaderia: null,
            ArticuloLibreUnidad: null,
          }
        : {}),
      ...(!esTextoLibre && valor ? { ArticuloLibreUnidad: null } : {}),
      ...(!valor
        ? {
            PrecioUnitario: 0,
            DescuentoLinea: 0,
            ErrorPrecio: "",
            IvaMercaderia: null,
            ArticuloLibreUnidad: null,
          }
        : {}),
    });
  };

  const agregarProductoAlComprobante = () => {
    const store = useStorePos.getState();
    const mercaderiaId = Number(store.Mercaderia?.Id) || 0;
    let agregado = false;

    if (mercaderiaId > 0) {
      store.agregarLinea();
      agregado = !useStorePos.getState().Mercaderia;
    } else {
      const descripcion = typeof store.Mercaderia === "string" ? store.Mercaderia.trim() : "";
      useStorePos.setState({
        ArticuloLibreDescripcion: descripcion,
        ArticuloLibreCantidad: store.Cantidad,
        ArticuloLibrePrecioUnitario: store.PrecioUnitario,
        ArticuloLibreDescuentoLinea: store.DescuentoLinea,
        ArticuloLibreCrearCatalogo: true,
      });
      agregado = useStorePos.getState().agregarArticuloLibre();
      if (agregado) {
        useStorePos.setState({
          Mercaderia: null,
          Cantidad: 0,
          PrecioUnitario: 0,
          DescuentoLinea: 0,
          ErrorPrecio: "",
          IvaMercaderia: null,
        });
      }
    }

    if (!agregado) return;
    if (modoRapido) {
      setEditorProductoVisible(true);
      window.setTimeout(() => productoSelectorRef.current?.querySelector("input")?.focus(), 80);
    } else {
      setEditorProductoVisible(false);
    }
  };

  return (
    <>
      <div className={`pos-bloque-form pos-datos-comprobante ${modoRapido ? "pos-datos-rapidos" : ""} ${datosExpandidos ? "expandido" : ""}`} style={{ position: "relative" }}>
        <div className="pos-bloque-titulo pos-datos-comprobante-titulo">
          <div className="pos-section-title">
            <span className="pos-section-icon">
              <i className="pi pi-file-edit" aria-hidden="true" />
            </span>
            <div>
              <h2 className="ing-subtitulo">{t("pos.saleData")}</h2>
              {modoRapido && (
                <p className="pos-datos-rapidos-resumen">
                  {ModoCliente === "consumidorFinal" ? t("pos.finalConsumer") : detalleCliente.razonSocial}
                  {opcionFacturacion?.Descripcion ? ` · ${opcionFacturacion.Descripcion}` : ""}
                </p>
              )}
            </div>
          </div>
          <div className="pos-datos-acciones">
            {modoRapido && (
              <button
                type="button"
                className="pos-btn-datos"
                aria-expanded={datosExpandidos}
                onClick={() => setDatosExpandidos((actual) => !actual)}
              >
                <i className={`pi ${datosExpandidos ? "pi-chevron-up" : "pi-sliders-h"}`} aria-hidden="true" />
                <span>{datosExpandidos ? t("pos.hide") : t("pos.changeData")}</span>
              </button>
            )}
            <button
              className="btn-limpiar"
              onClick={useStorePos.getState().limpiarTicket}
              title={t("pos.clearSale")}
            >
              <i className="pi pi-refresh" aria-hidden="true" />
              <span>{t("pos.restart")}</span>
            </button>
          </div>
        </div>

        <div className="pos-factura-grid">
          <div className="pos-cliente-modo pos-grid-ancho" role="group" aria-label={t("pos.customerType")}>
            <button
              type="button"
              className={!clienteNuevoActivo && ModoCliente === "consumidorFinal" ? "activo" : ""}
              aria-pressed={!clienteNuevoActivo && ModoCliente === "consumidorFinal"}
              onClick={clienteNuevoActivo ? cancelarClienteNuevo : () => useStorePos.getState().setConsumidorFinal()}
            >
              <i className="pi pi-bolt" aria-hidden="true" /> {t("pos.finalConsumer")}
            </button>
            <button
              type="button"
              className={clienteNuevoActivo || ModoCliente === "cliente" ? "activo" : ""}
              aria-pressed={clienteNuevoActivo || ModoCliente === "cliente"}
              onClick={() => clienteSelectorRef.current?.querySelector("input")?.focus()}
            >
              <i className="pi pi-user" aria-hidden="true" /> {t("pos.customer")}
            </button>
          </div>
          <div className="pos-campo">
            <div className="camp-o"><label>{t("pos.receiptType")}</label></div>
            <select
              value={NumeradorId || ""}
              disabled={CargandoOpcionesFacturacion || OpcionesFacturacion.length === 0}
              onChange={(e) => useStorePos.getState().seleccionarNumerador(e.target.value)}
            >
              <option value="">{CargandoOpcionesFacturacion ? t("common.loading") : t("pos.selectReceipt")}</option>
              {OpcionesFacturacion.map((opcion) => (
                <option key={opcion.NumeradorId} value={opcion.NumeradorId}>
                  {opcion.Letra} - {opcion.Descripcion}
                </option>
              ))}
            </select>
            {ErrorOpcionesFacturacion && <small className="pos-linea-error" role="alert">{ErrorOpcionesFacturacion}</small>}
          </div>
          <div className="pos-campo">
            <div className="camp-o"><label>{t("settings.pointOfSale")}</label></div>
            <div className="pos-input-lectura">{opcionFacturacion?.PuntoVenta || "—"}</div>
          </div>
          <div className="pos-campo">
            <div className="camp-o"><label>{t("pos.receiptNumber")}</label></div>
            <div className="pos-input-lectura">{numeroComprobante}</div>
          </div>
          <div className="pos-campo">
            <div className="camp-o"><label>{t("common.date")}</label></div>
            <div className="pos-input-lectura pos-fecha-lectura">
              <i className="pi pi-calendar" aria-hidden="true" />
              {fechaComprobante}
            </div>
          </div>
          <div className="pos-campo pos-cliente-selector">
            <div className="camp-o"><label>{t("pos.customer")}</label></div>
            <div className={`pos-cliente-control ${clienteNuevoActivo ? "es-nuevo" : ""}`} ref={clienteSelectorRef}>
              <div className="pos-cliente-input-wrap">
                <SelectorClientes
                  Lista={listaClientesSeleccionables}
                  Buscando={BuscandoClientes}
                  Error={ErrorClientes}
                  Reintentar={() => useStorePos.getState().getClientes({ forzar: true })}
                  setValor={seleccionarCliente}
                  Seleccionado={clienteNuevo || Cliente}
                  TituloNoSeleccionado={t("pos.searchOrEnterCustomer")}
                  Placeholder={t("pos.searchOrEnterCustomer")}
                  MostrarEstado={false}
                  MinCaracteresBusqueda={1}
                  CrearDesdeTexto={puedeCrearCliente ? iniciarClienteNuevo : undefined}
                />
                {clienteNuevoActivo && (
                  <span className="pos-cliente-nuevo-label">
                    <i className="pi pi-user-plus" aria-hidden="true" /> {t("pos.newCustomer")}
                  </span>
                )}
              </div>
            </div>
            {erroresClienteNuevo.sDescripcion && <small className="pos-linea-error">{erroresClienteNuevo.sDescripcion}</small>}
          </div>
          <div className="pos-campo">
            <div className="camp-o"><label>{clienteNuevoActivo ? t("pos.document") : detalleCliente.tipoDocumento || "CUIT"}</label></div>
            {clienteNuevoActivo && clienteNuevoRequiereDocumento ? (
              <>
                <div className="pos-cliente-documento-inline">
                  <select
                    aria-label={t("pos.documentType")}
                    value={clienteNuevo.TipoDocumentoId}
                    disabled={CargandoTiposDocumento || GuardandoCliente}
                    onChange={(event) => cambiarClienteNuevo("TipoDocumentoId", event.target.value)}
                  >
                    <option value="">{CargandoTiposDocumento ? t("common.loading") : t("pos.documentType")}</option>
                    {TiposDocumento.map((tipo) => (
                      <option key={tipo.Id} value={tipo.Id}>{tipo.sDescripcion || tipo.Descripcion}</option>
                    ))}
                  </select>
                  <input
                    aria-label={clienteNuevoRequiereCuit ? "CUIT" : t("pos.document")}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder={clienteNuevoRequiereCuit ? "CUIT" : t("pos.documentNumber")}
                    value={clienteNuevo.Documento}
                    disabled={GuardandoCliente}
                    onChange={(event) => cambiarClienteNuevo("Documento", event.target.value.replace(/[^\d-]/g, ""))}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        guardarClienteNuevo();
                      }
                    }}
                  />
                </div>
                {(erroresClienteNuevo.TipoDocumentoId || erroresClienteNuevo.Documento) && (
                  <small className="pos-linea-error">{erroresClienteNuevo.TipoDocumentoId || erroresClienteNuevo.Documento}</small>
                )}
              </>
            ) : clienteNuevoActivo ? (
              <div className="pos-input-lectura pos-documento-no-requerido">{t("pos.notRequiredForAmount")}</div>
            ) : (
              <div className="pos-input-lectura">{detalleCliente.documento}</div>
            )}
          </div>
          <div className="pos-campo">
            <div className="camp-o"><label>{t("pos.vatCondition")}</label></div>
            {clienteNuevoActivo ? (
              <>
                <select
                  value={clienteNuevo.CondicionIVAId}
                  disabled={CargandoCondicionesIVA || GuardandoCliente}
                  aria-label={t("pos.newCustomerVatCondition")}
                  onChange={(event) => cambiarClienteNuevo("CondicionIVAId", event.target.value)}
                >
                  <option value="">{CargandoCondicionesIVA ? t("common.loading") : t("pos.selectCondition")}</option>
                  {CondicionesIVA.map((condicion) => (
                    <option key={condicion.Id} value={condicion.Id}>{condicion.sDescripcion || condicion.Descripcion}</option>
                  ))}
                </select>
                {erroresClienteNuevo.CondicionIVAId && <small className="pos-linea-error">{erroresClienteNuevo.CondicionIVAId}</small>}
              </>
            ) : (
              <div className="pos-input-lectura">{CondicionIVASeleccionada || detalleCliente.condicionIva}</div>
            )}
          </div>
          <div className="pos-campo">
            <div className="camp-o"><label>{t("pos.paymentMethod")}</label></div>
            <div className="pos-input-lectura">{t("pos.definedAtCheckout")}</div>
          </div>
          {clienteNuevoActivo && (
            <div className="pos-cliente-nuevo-aviso pos-grid-ancho" role="status">
              <i className="pi pi-check-circle" aria-hidden="true" />
              <div>
                <strong>{t("pos.newCustomerWillBeSaved")}</strong>
                <span>
                  {clienteNuevoRequiereDocumento
                    ? t("pos.documentRequiredByAmount", { amount: formatearMoneda(LIMITE_DOCUMENTO_CLIENTE_NUEVO) })
                    : t("pos.selectVatAndCreate")}
                </span>
              </div>
              <div className="pos-cliente-nuevo-acciones">
                <button type="button" className="pos-btn-cliente-cancelar" onClick={cancelarClienteNuevo} disabled={GuardandoCliente}>
                  {t("common.cancel")}
                </button>
                <button type="button" className="pos-btn-cliente-crear" onClick={guardarClienteNuevo} disabled={GuardandoCliente || (clienteNuevoRequiereDocumento && CargandoTiposDocumento) || CargandoCondicionesIVA}>
                  <i className={GuardandoCliente ? "pi pi-spin pi-spinner" : "pi pi-user-plus"} aria-hidden="true" />
                  {GuardandoCliente ? t("pos.creating") : t("pos.createAndUseCustomer")}
                </button>
              </div>
            </div>
          )}
          {ModoCliente === "consumidorFinal" && RequiereIdentificacionFiscal && (
            <div className="pos-identificacion-alerta pos-grid-ancho" role="alert">
              <i className="pi pi-exclamation-triangle" aria-hidden="true" />
              <div>
                <strong>{t("pos.buyerIdentificationRequired")}</strong>
                <span>
                  {SolicitaIdentificacionFiscal
                    ? t("pos.taxIdRequested")
                    : t("pos.arcaIdentificationLimit", { amount: formatearMoneda(MontoConsumidorFinalSinIdentificar) })}
                  {" "}{t("pos.selectOrCreateCustomerCartPreserved")}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="pos-cliente-compacto">
          <div className="pos-fila-flex">
            <div className="pos-campo pos-campo-ancho">
              <div className="camp-o">
                <label>{t("pos.customer")}:</label>
              </div>
              <SelectorClientes
                Lista={ListaClientes}
                Buscando={BuscandoClientes}
                Error={ErrorClientes}
                Reintentar={() => useStorePos.getState().getClientes({ forzar: true })}
                setValor={seleccionarCliente}
                Seleccionado={clienteNuevo || Cliente}
                TituloNoSeleccionado={t("pos.searchOrEnterCustomer")}
                MostrarEstado={false}
                MinCaracteresBusqueda={1}
                CrearDesdeTexto={puedeCrearCliente ? iniciarClienteNuevo : undefined}
              />
            </div>
          </div>

          <div className="pos-cliente-ficha">
            <div className="pos-cliente-ficha-cabecera">
              <strong>{t("pos.commercialInformation")}</strong>
              {detalleCliente.usaDatosTemporales && (
                <span title={t("pos.sampleDataHelp")}>
                  {t("pos.sampleData")}
                </span>
              )}
            </div>
            <dl className="pos-cliente-datos">
              <div>
                <dt>{t("common.code")}</dt>
                <dd>{detalleCliente.codigo}</dd>
              </div>
              <div className="pos-cliente-dato-ancho">
                <dt>{t("pos.businessName")}</dt>
                <dd>{detalleCliente.razonSocial}</dd>
              </div>
              <div>
                <dt>{t("pos.document")}</dt>
                <dd>
                  <small>{detalleCliente.tipoDocumento}</small>
                  {detalleCliente.documento}
                </dd>
              </div>
              <div>
                <dt>{t("pos.vatStatus")}</dt>
                <dd>{detalleCliente.condicionIva}</dd>
              </div>
              <div className="pos-cliente-dato-ancho">
                <dt>{t("pos.seller")}</dt>
                <dd>{detalleCliente.vendedor}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <div className={`pos-bloque-form pos-productos-bloque ${editorProductoVisible ? "editor-abierto" : ""}`}>
        <div className="pos-bloque-titulo">
          <div className="pos-section-title">
            <span className="pos-section-icon">
              <i className="pi pi-shopping-bag" aria-hidden="true" />
            </span>
            <div>
              <h2 className="ing-subtitulo">{t("pos.productDetails")}</h2>
            </div>
          </div>
          <div className="pos-detalle-acciones pos-producto-modos" role="group" aria-label={t("pos.productType")}>
            <button
              type="button"
              className={`pos-btn-agregar-producto ${editorProductoVisible ? "activo" : ""}`}
              aria-pressed={editorProductoVisible}
              onClick={alternarEditorProducto}
            >
              <i className={`pi ${editorProductoVisible ? "pi-times" : "pi-plus"}`} aria-hidden="true" />
              <span>{t("pos.addProduct")}</span>
            </button>
          </div>
        </div>

        {editorProductoVisible && (
        <div className="pos-producto-catalogo">
          <div className="pos-producto-grid">
            <div className="pos-campo pos-campo-ancho pos-producto-selector" ref={productoSelectorRef}>
              <div className="camp-o">
                <span className="mensaje-obligatorio">*</span>
                <label>{t("pos.productOrDescription")}</label>
              </div>
              <div className={`pos-producto-input-wrap ${esArticuloNuevo ? "es-nuevo" : ""}`}>
                <BuscadorMercaderias
                  Buscar={useStorePos.getState().getMercaderia}
                  LimpiarResultados={useStorePos.getState().limpiarResultadosMercaderias}
                  setValor={cambiarProductoEditor}
                  Seleccionado={Mercaderia}
                  TituloNoSeleccionado={t("pos.searchOrEnterProduct")}
                />
                {esArticuloNuevo && (
                  <span
                    className="pos-producto-nuevo-aviso"
                    title={t("pos.newItemWillBeCreated", { description: descripcionArticuloNuevo })}
                  >
                    <i className="pi pi-plus-circle" aria-hidden="true" />
                    {t("pos.newItem")}
                  </span>
                )}
              </div>
            </div>

            <div className="pos-campo pos-producto-cantidad">
              <div className="camp-o pos-label-row">
                <span className="pos-label-main">
                  {Number(Cantidad) === 0 && <span className="mensaje-obligatorio">*</span>}
                  <label>{t("pos.quantityOrWeight")}</label>
                </span>
                <span className={`pos-stock-chip ${stockEstado}`} title={t("pos.stockLabel", { stock: stockTexto })} aria-label={t("pos.stockLabel", { stock: stockTexto })}>
                  {stockTexto}
                </span>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={Cantidad}
                onChange={(e) => useStorePos.setState({ Cantidad: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="pos-campo pos-producto-precio">
              <div className="camp-o">
                {precioNumero <= 0 && <span className="mensaje-obligatorio">*</span>}
                <label>{esArticuloNuevo ? t("pos.finalUnitPrice") : t("pos.unitPrice")}</label>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={PrecioUnitario}
                placeholder={BuscandoPrecio ? t("pos.searching") : "0.00"}
                readOnly={esProductoExistente}
                disabled={esProductoExistente && BuscandoPrecio}
                onChange={(e) => {
                  if (!esProductoExistente) useStorePos.setState({ PrecioUnitario: e.target.value });
                }}
              />
              {ErrorPrecio && <small className="pos-linea-error">{ErrorPrecio}</small>}
            </div>

            <div className="pos-campo pos-producto-descuento">
              <div className="camp-o">
                <label>{t("pos.lineDiscount")}</label>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={DescuentoLinea}
                onChange={(e) => useStorePos.setState({ DescuentoLinea: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="pos-campo pos-producto-iva">
              <div className="camp-o"><label>{t("pos.productVat")}</label></div>
              <div className="pos-dato-solo-lectura" aria-label={t("pos.readOnlyVat")}>
                {esArticuloNuevo
                  ? CategoriaFiscalProductoNuevo
                    ? `${CategoriaFiscalProductoNuevo.PorcentajeIVA}% · ${t("pos.automatic")}`
                    : t("pos.selectCategory")
                  : !esProductoExistente
                  ? t("pos.selectValue")
                  : IvaMercaderia?.valido === false
                    ? IvaMercaderia.error || t("pos.vatNotConfigured")
                    : IvaMercaderia?.conocido === false
                      ? t("pos.checkingVat")
                    : IvaMercaderia?.pendiente
                      ? t("pos.configurationPending")
                    : IvaMercaderia?.valido
                      ? `${IvaMercaderia.PorcentajeIVA}%`
                      : t("pos.checkingVat")}
              </div>
            </div>
          </div>

          {esArticuloNuevo && (
            <div className="pos-producto-nuevo-opciones">
              <div className="pos-campo">
                <div className="camp-o">
                  <span className="mensaje-obligatorio">*</span>
                  <label>{t("pos.unitOfMeasure")}</label>
                </div>
                <select
                  value={ArticuloLibreUnidad?.Id || ""}
                  disabled={BuscandoUnidades}
                  onChange={(e) => {
                    const unidad = ListaUnidades.find(
                      (item) => String(item.Id) === e.target.value
                    ) || null;
                    useStorePos.getState().setArticuloLibreCampo("ArticuloLibreUnidad", unidad);
                  }}
                >
                  <option value="">
                    {BuscandoUnidades ? t("pos.loadingUnits") : t("pos.selectUnit")}
                  </option>
                  {ListaUnidades.map((unidad) => (
                    <option key={unidad.Id} value={unidad.Id}>
                      {unidad.sDescripcion}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pos-campo pos-categoria-fiscal">
                <div className="camp-o">
                  <span className="mensaje-obligatorio">*</span>
                  <label>{t("pos.taxCategory")}</label>
                </div>
                <select
                  value={CategoriaFiscalProductoNuevo?.Id || ""}
                  disabled={CargandoCategoriasFiscales}
                  onChange={(event) => {
                    const categoria = ListaCategoriasFiscales.find(
                      (item) => String(item.Id) === event.target.value
                    ) || null;
                    useStorePos.getState().setArticuloLibreCampo("CategoriaFiscalProductoNuevo", categoria);
                  }}
                >
                  <option value="">
                    {CargandoCategoriasFiscales ? t("pos.loadingCategories") : t("pos.selectCategory")}
                  </option>
                  {ListaCategoriasFiscales.map((categoria) => (
                    <option key={`${categoria.Id}-${categoria.Version}`} value={categoria.Id}>
                      {formatearCategoriaFiscal(categoria)}
                    </option>
                  ))}
                </select>
                {ErrorCategoriasFiscales && <small className="pos-linea-error">{ErrorCategoriasFiscales}</small>}
              </div>
              <div className="pos-producto-nuevo-catalogo">
                <div className="pos-catalogo-confirmacion" role="status">
                  <i className="pi pi-check-circle" aria-hidden="true" />
                  <div>
                    <strong>{t("pos.newProductWillBeSaved")}</strong>
                    <small>{t("pos.newProductDetail")}</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="pos-accion-ticket">
            <div className="pos-linea-preview" aria-live="polite">
              <span>{esArticuloNuevo ? t("pos.lineTotalVatIncluded") : IvaMercaderia?.pendiente ? t("pos.lineTotalFinalPrice") : t("pos.lineTotalWithVat")}</span>
              <strong>{totalLineaTexto}</strong>
            </div>
            <button onClick={agregarProductoAlComprobante} className="pos-btn-agregar">
              <i className="pi pi-cart-plus" aria-hidden="true" />
              <span>{t("pos.addToReceipt")}</span>
            </button>
          </div>
        </div>
        )}
      </div>
    </>
  );
};

export default PosVenta;
